import React, { useEffect, useReducer, useCallback, useMemo } from "react"
import {
  signIn,
  signUp,
  signOut,
  getCurrentUser,
  updateUserAttributes,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  fetchAuthSession,
} from "aws-amplify/auth"
import { Amplify } from "aws-amplify"
// config
import { AMPLIFY_API } from "src/config-global"
// api
import { verifyAccount } from 'src/api/server/account'
import { getProfile, loginCashRegister, updateProfile, verifyCashRegister } from 'src/api/server/user'
import { getCurrentSession } from 'src/api/server/cashRegister'
import { initializeApp } from 'src/api/server/init'
import { syncWithERP } from 'src/api/server/product'
import { isSessionValidForOperations, isCashierRole } from 'src/utils/cash-register'
import { getInitCache, saveInitCache, hasValidCache, clearInitCache } from 'src/utils/indexeddb-cache'
//
import { AuthContext } from "./auth-context"
import { ActionMapType, AuthStateType, AuthUserType, RegisterParam } from "../../types"

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = "INITIAL",
  LOGOUT = "LOGOUT",
  SESSION_CHECKED = "SESSION_CHECKED",
  USER_DATA_READY = "USER_DATA_READY",
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.SESSION_CHECKED]: {
    valid: boolean;
    requiresAction: boolean;
  };
  [Types.USER_DATA_READY]: {
    ready: boolean;
  };
}

type Action = ActionMapType<Payload>[keyof ActionMapType<Payload>]

const initialState: AuthStateType = {
  user: null,
  loading: true,
  sessionChecked: false,
  sessionValid: false,
  requiresSessionAction: false,
  userDataReady: false,
}

const reducer = (state: AuthStateType, action: Action) => {
  if (action.type === Types.INITIAL) {
    return {
      ...state,
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
      sessionChecked: false,
      sessionValid: false,
      requiresSessionAction: false,
      userDataReady: false,
    };
  }
  if (action.type === Types.SESSION_CHECKED) {
    return {
      ...state,
      sessionChecked: true,
      sessionValid: action.payload.valid,
      requiresSessionAction: action.payload.requiresAction,
    };
  }
  if (action.type === Types.USER_DATA_READY) {
    return {
      ...state,
      userDataReady: action.payload.ready,
    };
  }
  return state;
}

// ----------------------------------------------------------------------

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: AMPLIFY_API.userPoolId || '',
      userPoolClientId: AMPLIFY_API.userPoolWebClientId || '',
      // region: AMPLIFY_API.region,
      loginWith: {
        email: true,
        phone: false,
      },
    }
  }
})

type Props = {
  children: React.ReactNode
}

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const updateCurrentUser = useCallback(
    async (data: any) => {
      if (!data) data = await getProfile(state.user?.id);
      const dataUser = data?.data || data;
      try {
        const cashRegister = await verifyCashRegister(state.user?.id);
        console.log('AuthProvider cashRegister', cashRegister);
        dataUser.cashRegister = cashRegister.data;
      } catch (error) {
        console.error('cashRegister Error', error);
      }
      dispatch({ type: Types.INITIAL, payload: { user: dataUser } });
    },
    [state.user?.id]
  );

  const updateUser = useCallback(
    async (data: any) => {
      try {
        const fields = {
          given_name: 'firstName',
          family_name: 'lastName',
          phone_number: 'phone',
        };
        const updatedAttributes = {} as any;
        Object.entries(fields).forEach(([field, value]) => {
          updatedAttributes[field] = data[value];
        });
        const currentUser = await getCurrentUser();
        await updateUserAttributes({ userAttributes: updatedAttributes });
        console.log('User attributes updated successfully');
        const updated = await updateProfile(currentUser.userId, data);
        console.info('updated', updated);
        await updateCurrentUser(updated.data);
      } catch (error) {
        console.error('Error updating user attributes', error);
      }
    },
    [updateCurrentUser]
  );

  // Timeout helper pour éviter les blocages sur les appels API
  // NOTE : Les timeouts sont désactivés en production pour éviter les erreurs réseau
  const withTimeout = async <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
    // En production, on n'applique pas de timeout pour laisser les requêtes se terminer
    if (import.meta.env.PROD) {
      try {
        return await promise;
      } catch (err) {
        console.warn(`⚠️ [API] Error:`, err.message || err);
        return fallback;
      }
    }
    // En dev, on garde les timeouts pour détecter les problèmes
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout after ${ms}ms`)), ms)
        ),
      ]);
    } catch (err_1) {
      console.warn(`⚠️ [TIMEOUT] API call timed out:`, err_1.message);
      // Retourner le fallback sans erreur critique - permet à l'app de continuer
      return fallback;
    }
  };

  // ============================================================
  // NOUVELLE INITIALISATION OPTIMISÉE AVEC CACHE
  // Strategy: Cache-First avec revalidation en arrière-plan
  // ============================================================
  const initialize = useCallback(async () => {
    console.log('🔄 [INIT] Starting optimized initialization...');
    const initStartTime = Date.now();

    try {
      const currentUser = await withTimeout(
        getCurrentUser(),
        5000, // 5s timeout for auth check
        null
      );
      console.log('✅ [INIT] currentUser found:', currentUser);

      if (currentUser) {
        // ============================================================
        // ÉTAPE 1: CHARGER IMMÉDIATEMENT DEPUIS LE CACHE (si disponible)
        // Cela permet d'afficher l'UI instantanément
        // ============================================================
        const cachedInit = await getInitCache();
        if (cachedInit?.user) {
          console.log('💾 [INIT] Loading from IndexedDB cache...');

          dispatch({
            type: Types.INITIAL,
            payload: { user: cachedInit.user },
          });

          dispatch({
            type: Types.USER_DATA_READY,
            payload: { ready: true },
          });

          // Dispatcher les données du cache vers Redux
          window.dispatchEvent(
            new CustomEvent('ws:init-complete', {
              detail: {
                roles: cachedInit.roles || [],
                accesses: cachedInit.accesses || [],
                supplies: cachedInit.supplies || [],
              },
            })
          );

          // Dispatcher aussi les produits/users du cache
          if (cachedInit.products?.length > 0) {
            window.dispatchEvent(
              new CustomEvent('ws:init-data-complete', {
                detail: { entity: 'products', data: cachedInit.products },
              })
            );
          }
          if (cachedInit.users?.length > 0) {
            window.dispatchEvent(
              new CustomEvent('ws:init-data-complete', {
                detail: { entity: 'users', data: cachedInit.users },
              })
            );
          }

          console.log('✅ [INIT] Cache loaded in', Date.now() - initStartTime, 'ms');
        }

        // ============================================================
        // ÉTAPE 2 : REQUÊTE API EN ARRIÈRE-PLAN (revalidation)
        // Récupère les données fraîches et met à jour le cache
        // ============================================================
        const loadUserData = async () => {
          try {
            const hadCache = await hasValidCache();
            if (hadCache) {
              console.log('🔄 [INIT] Revalidating data in background...');
            } else {
              console.log('🔄 [INIT] Loading optimized init data (no cache)...');
            }

            // APPEL UNIQUE : Toutes les données essentielles en une requête
            const initResult = await withTimeout(
              initializeApp(currentUser.userId),
              30000, // 30s timeout pour l'init
              { data: null }
            );

            const initData = initResult?.data;
            if (!initData?.user) {
              if (!hadCache) {
                console.warn('⚠️ [INIT] No init data received and no cache, falling back');
                return await loadUserDataLegacy();
              }
              console.warn('⚠️ [INIT] No init data but cache available, keeping cache');
              return;
            }

            const { user, roles, accesses, supplies, meta } = initData;

            console.log('✅ [INIT] Fresh data received:', {
              streamingEnabled: meta?.streamingEnabled,
              responseTime: meta?.responseTime,
              totalTime: Date.now() - initStartTime,
              hadCache,
              user
            });

            // Stocker le logo si présent
            if (user.configSelected?.logo) {
              window.localStorage.setItem('LOGO', user.configSelected.logo);
            }

            if (!user.id) {
              user.id = currentUser.userId;
            }

            // Dispatch avec les données fraîches (même si identiques, pour être sûr)
            dispatch({
              type: Types.INITIAL,
              payload: { user },
            });

            const hasUserData = !!(user.configs && user.accesses);
            dispatch({
              type: Types.USER_DATA_READY,
              payload: { ready: hasUserData },
            });

            // Dispatcher les données fraîches pour le store Redux
            window.dispatchEvent(
              new CustomEvent('ws:init-complete', {
                detail: { roles, accesses, supplies, fresh: true },
              })
            );

            // sauvegarder dans le cache IndexedDB
            await saveInitCache({
              user,
              roles: roles || [],
              accesses: accesses || [],
              supplies: supplies || [],
              products: [], // Sera rempli par WebSocket
              users: [],    // Sera rempli par WebSocket
              configSelected: user.configSelected,
              meta: {
                cachedAt: Date.now(),
                version: '1.0',
              },
            });

            // Premier démarrage quotidien : lancer une synchro COMPLETE des
            // articles NetSuite en arrière-plan. L'endpoint diffuse ensuite
            // chaque lot par WebSocket, donc l'interface se met à jour sans
            // refresh ni navigation vers la page Produits.
            const selectedConfigId = user.configSelected?.id || user.configSelected?._id || user.configSelected?.config;
            if (selectedConfigId) {
              const today = new Date().toISOString().slice(0, 10);
              const dailyProductSyncKey = `pos360:full-product-sync:${selectedConfigId}:${today}`;
              if (!window.localStorage.getItem(dailyProductSyncKey)) {
                // Marquer avant l'appel pour éviter plusieurs syncs simultanées
                // si plusieurs composants déclenchent une réinitialisation.
                window.localStorage.setItem(dailyProductSyncKey, String(Date.now()));
                syncWithERP(currentUser.userId, selectedConfigId, true).catch((syncError) => {
                  console.warn('⚠️ [INIT] Full daily ERP product sync failed:', syncError);
                  // Permettre une nouvelle tentative au prochain login/refresh.
                  window.localStorage.removeItem(dailyProductSyncKey);
                });
              }
            }

            // Vérifier la session de caisse pour les caissiers
            if (isCashierRole(user.role)) {
              withTimeout(getCurrentSession(currentUser.userId), 5000, null)
                .then((sessionResult: any) => {
                  if (sessionResult?.data) {
                    const validation = isSessionValidForOperations(sessionResult.data);
                    dispatch({
                      type: Types.SESSION_CHECKED,
                      payload: {
                        valid: validation.valid,
                        requiresAction: !validation.valid,
                      },
                    });
                  } else {
                    dispatch({
                      type: Types.SESSION_CHECKED,
                      payload: { valid: false, requiresAction: true },
                    });
                  }
                })
                .catch((sessionError) => {
                  console.warn('⚠️ [INIT] Session check error:', sessionError);
                  dispatch({
                    type: Types.SESSION_CHECKED,
                    payload: { valid: false, requiresAction: true },
                  });
                });
            } else {
              dispatch({
                type: Types.SESSION_CHECKED,
                payload: { valid: true, requiresAction: false },
              });
            }

            console.log('✅ [INIT] Revalidation completed in', Date.now() - initStartTime, 'ms');
          } catch (error) {
            console.error('⚠️ [INIT] Error loading init data:', error);
            // Si on a un cache, on garde le cache même si l'API échoue
            const hadCache = await hasValidCache();
            if (!hadCache) {
              return await loadUserDataLegacy();
            }
            console.warn('⚠️ [INIT] API failed but cache is available, keeping cache');
          }
        };

        // Fallback legacy si tout échoue
        const loadUserDataLegacy = async () => {
          console.log('🔄 [INIT] Fallback to legacy loading...');
          try {
            const [profileResult, cashRegisterResult] = await Promise.all([
              withTimeout(getProfile(currentUser.userId), 20000, { data: null }),
              withTimeout(verifyCashRegister(currentUser.userId), 15000, { data: null }),
            ]);

            const fullUser = profileResult.data;
            if (!fullUser) {
              dispatch({ type: Types.INITIAL, payload: { user: null } });
              return;
            }

            fullUser.cashRegister = cashRegisterResult.data;
            dispatch({ type: Types.INITIAL, payload: { user: fullUser } });
          } catch (error) {
            console.error('❌ [INIT] Legacy fallback also failed:', error);
            dispatch({ type: Types.INITIAL, payload: { user: null } });
          }
        };

        // Lancer le chargement en arrière-plan
        loadUserData().then();
      } else {
        console.log('❌ [INIT] No current user found');
        dispatch({
          type: Types.INITIAL,
          payload: { user: null },
        });
      }
    } catch (error) {
      console.error('❌ [INIT] Error during initialization:', error);
      dispatch({
        type: Types.INITIAL,
        payload: { user: null },
      });
    }
  }, []);

  useEffect(() => {
    console.log('🚀 [AUTH] Component mounted, initializing...');
    initialize().then();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (email: string, password: string) => {
      console.log('🔐 [LOGIN] Attempting login for:', email);
      await signIn({ username: email, password });
      console.log('✅ [LOGIN] Sign in successful');
      await initialize();
    },
    [initialize]
  );

  const cashRegisterLogin = useCallback(
    async (cashRegisterId: string) => {
      const crl = await loginCashRegister(state?.user?.id, cashRegisterId);
      console.log('cashRegisterLogin', crl);
      window.localStorage.setItem('CASH-REGISTER-TOKEN', crl.cashRegister.token);
      crl.cashRegister.token = undefined;
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: { ...(state.user || {}), ...(crl || {}) },
        },
      });

      // Force session check immediately after login
      try {
        const session = await getCurrentSession(state?.user?.id, cashRegisterId);
        console.log('[cashRegisterLogin] Current session:', session);

        // Check if session needs action
        const { isSessionValidForOperations } = await import('src/utils/cash-register');
        const validation = isSessionValidForOperations(session?.data);

        if (!validation.valid) {
          console.log('[cashRegisterLogin] Session needs action, setting flag to show dialog');
          // Set a flag to force dialog to open
          window.localStorage.setItem('FORCE_SESSION_DIALOG', 'true');
        }
      } catch (error) {
        console.log('[cashRegisterLogin] Error fetching session, will force dialog:', error);
        // If error, assume session needs to be opened
        window.localStorage.setItem('FORCE_SESSION_DIALOG', 'true');
      }
    },
    [state.user]
  );

  // REGISTER
  const register = useCallback(async (data: RegisterParam) => {
    const { email, password, lastName, firstName, netsuiteAccount, phoneNumber } = data;
    const verified = await verifyAccount(netsuiteAccount);
    console.info('account verified', verified);
    if (verified) {
      const signUpOutput = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
            phone_number:
              (phoneNumber && ((!phoneNumber?.startsWith('+') && '+') || '') + phoneNumber) ||
              undefined,
          },
        },
      });
      console.info('signUp', signUpOutput);
      return signUpOutput;
    }
    throw new Error('This NetSuite account is not registered');
  }, []);

  // CONFIRM REGISTER
  const confirmRegister = useCallback(async (email: string, code: string) => {
    await confirmSignUp({ username: email, confirmationCode: code });
  }, []);

  // RESEND CODE REGISTER
  const resendCodeRegister = useCallback(async (email: string) => {
    await resendSignUpCode({ username: email });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    console.log('🚪 [LOGOUT] Logging out...');

    // Clear IndexedDB cache FIRST to prevent auto-reconnect with old account
    // This must happen before any early returns
    await clearInitCache();
    console.log('🗑️ [LOGOUT] IndexedDB cache cleared');

    // Clear all AWS Cognito data from localStorage
    const cognitoKeys = Object.keys(localStorage).filter(key =>
      key.startsWith('CognitoIdentityServiceProvider') ||
      key.startsWith('aws-amplify')
    );
    cognitoKeys.forEach(key => localStorage.removeItem(key));
    console.log('🗑️ [LOGOUT] AWS Cognito localStorage cleared:', cognitoKeys.length, 'keys');

    // Clear Redux persist data from IndexedDB
    try {
      const { deleteDB } = await import('../../../redux/indexeddb-storage');
      await deleteDB('pos360-mini-db');
      console.log('🗑️ [LOGOUT] Redux IndexedDB cleared');
    } catch (error) {
      console.warn('[LOGOUT] Failed to clear Redux IndexedDB:', error);
    }

    // Clear all auth-related localStorage items
    const authKeys = [
      'CASH-REGISTER-TOKEN',
      'FORCE_SESSION_DIALOG',
      'FORCE_CLOSE_SESSION_DIALOG',
      'PENDING_CASH_REGISTER_ID',
      'LOGO',
    ];
    authKeys.forEach(key => localStorage.removeItem(key));
    console.log('🗑️ [LOGOUT] Auth localStorage cleared');

    // Force cash register session closing if open
    try {
      const session = await getCurrentSession(state?.user?.id, state?.user?.cashRegisterId);
      if (session?.data && session.data.status === 'open') {
        console.log('[logout] Session is open, forcing close dialog');
        // Set flag to force close dialog
        window.localStorage.setItem('FORCE_CLOSE_SESSION_DIALOG', 'true');
      }
    } catch (error) {
      console.log('[logout] Error checking session, proceeding with logout:', error);
    }

    // Always complete the logout, regardless of session status
    await signOut();
    dispatch({
      type: Types.LOGOUT,
    });
    // Clear session flags
    window.localStorage.removeItem('FORCE_SESSION_DIALOG');
    window.localStorage.removeItem('FORCE_CLOSE_SESSION_DIALOG');
  }, [state.user]);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await resetPassword({ username: email });
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(async (email: string, code: string, password: string) => {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword: password });
  }, []);

  // CHANGE PASSWORD
  const changePassword = useCallback(async (currPWD: string, newPWD: string) => {
    await updatePassword({ oldPassword: currPWD, newPassword: newPWD });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  // SELECT CONFIG
  const onSelectConfig = useCallback(
    async (configId: string) => {
      const { user } = state;

      if (user) {
        console.info('onSelectConfig user', user);

        user.configSelected = user.configs.find((config: any) => config.id === configId);

        dispatch({
          type: Types.INITIAL,
          payload: { user },
        });
      }
    },
    [state]
  );

  const setCashRegisterId = useCallback(
    async (cashRegisterId: string | null) => {
      const { user } = state;
      if (user) {
        // If changing cash register, force session closing first
        if (user.cashRegisterId && cashRegisterId && user.cashRegisterId !== cashRegisterId) {
          try {
            const session = await getCurrentSession(user.id, user.cashRegisterId);
            if (session?.data && session.data.status === 'open') {
              console.log('[setCashRegisterId] Session is open, forcing close dialog');
              // Set flag to force close dialog
              window.localStorage.setItem('FORCE_CLOSE_SESSION_DIALOG', 'true');
              window.localStorage.setItem('PENDING_CASH_REGISTER_ID', cashRegisterId);
              // Don't change register yet - let the dialog handle it
              return;
            }
          } catch (error) {
            console.log('[setCashRegisterId] Error checking session, proceeding with change:', error);
          }
        }

        user.cashRegisterId = cashRegisterId;
        dispatch({
          type: Types.INITIAL,
          payload: { user },
        });

        // If setting a new cash register, force session check
        if (cashRegisterId) {
          try {
            const session = await getCurrentSession(user.id, cashRegisterId);
            console.log('[setCashRegisterId] Current session after change:', session);

            // Check if session needs action
            const { isSessionValidForOperations } = await import('src/utils/cash-register');
            const validation = isSessionValidForOperations(session?.data);

            if (!validation.valid) {
              console.log('[setCashRegisterId] Session needs action, setting flag to show dialog');
              window.localStorage.setItem('FORCE_SESSION_DIALOG', 'true');
            }
          } catch (error) {
            console.log('[setCashRegisterId] Error fetching session, forcing dialog:', error);
            window.localStorage.setItem('FORCE_SESSION_DIALOG', 'true');
          }
        }
      }
    },
    [state]
  );

  // Vérification de session périodique - uniquement en dev pour debugging, 5 minutes
  useEffect(() => {
    // Vérifier une seule fois au montage, pas en intervalle
    const checkSessionOnce = async () => {
      try {
        const session = await fetchAuthSession();
        console.log('🔍 [SESSION] Valid:', session.tokens?.accessToken !== undefined);
      } catch (error) {
        console.warn('⚠️ [SESSION] Check error:', error);
      }
    };

    // Vérification unique au montage
    checkSessionOnce().then();

    // Intervalle uniquement si explicitement activé via localStorage (debug mode)
    const debugMode = window.localStorage.getItem('DEBUG_SESSION_CHECK') === 'true';
    if (debugMode) {
      const interval = setInterval(checkSessionOnce, 5 * 60 * 1000); // 5 minutes
      return () => clearInterval(interval);
    }
  }, []);

  const memorizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'amplify',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      sessionChecked: state.sessionChecked,
      sessionValid: state.sessionValid,
      requiresSessionAction: state.requiresSessionAction,
      userDataReady: state.userDataReady,
      //
      login,
      logout,
      register,
      updateUser,
      newPassword,
      onSelectConfig,
      setCashRegisterId,
      forgotPassword,
      confirmRegister,
      updateCurrentUser,
      resendCodeRegister,
      cashRegisterLogin,
      changePassword,
    }),
    [
      state.user,
      status,
      state.sessionChecked,
      state.sessionValid,
      state.requiresSessionAction,
      state.userDataReady,
      login,
      logout,
      register,
      updateUser,
      newPassword,
      forgotPassword,
      confirmRegister,
      updateCurrentUser,
      resendCodeRegister,
      onSelectConfig,
      setCashRegisterId,
      cashRegisterLogin,
      changePassword,
    ]
  );

  return <AuthContext.Provider value={memorizedValue}>{children}</AuthContext.Provider>;
}
